// Central company configuration. All owner-confirmed values live in COMPANY.
// Remaining placeholders (team names on About) render visually flagged via <Token />.

export const COMPANY = {
  name: 'IDB HVAC Solutions',
  legalName: 'ID Builder HVAC Solutions LLC',
  alternateName: 'IDB HVAC',
  founderName: 'Sebastian Suciu',
  foundingDate: '2025-01-31', // matches the Google Business Profile opening date
  phoneMain: '425-405-5365',
  phoneMainHref: 'tel:+14254055365',
  email: 'Sebastian@idbhvac.com',
  license: 'IDBHVH*754BU',
  licenseLine: 'Licensed, Bonded & Insured — WA Lic. #IDBHVH*754BU',
  hours: 'Mon–Fri 7:00 AM – 5:00 PM',
  siteUrl: 'https://idbhvac.com',
  addressFederalWay: '36058 21st Ln S',
  yearsInBusiness: '2',
  financingProviders: 'Acorn Finance and HFS Financial', // HFS brief: the two partners — the list itself lives in src/config/financing.js
  brands: ['Mitsubishi Electric', 'Daikin'],
};

/** Public profiles — used for schema.org `sameAs` and the footer. Keep in step with the Google Business Profile. */
export const SOCIAL_LINKS = {
  googleMaps: 'https://www.google.com/maps?cid=16950663102576591112',
  googleReview: 'https://g.page/r/CQgBZohI2jzrEBM/review',
  linkedin: 'https://www.linkedin.com/company/idbhvac/',
  facebook: 'https://www.facebook.com/idbhvac',
  instagram: 'https://www.instagram.com/idbhvac',
  yelp: 'https://www.yelp.com/biz/idb-hvac-solutions-federal-way',
};

/**
 * The ID Builder family of companies (SEO Batch B, Part 2): the footer "Our companies" column and
 * the sentence on /about. Owner-supplied names, URLs and one-line descriptions — nothing else.
 */
export const COMPANY_FAMILY = [
  { key: 'hvac', name: 'IDB HVAC Solutions', href: '/', desc: 'Heating and cooling installation — this site', internal: true },
  { key: 'construction', name: 'ID Builder Construction', href: 'https://idbuilderconstruction.com', desc: 'General contracting, remodels and new construction' },
  { key: 'plumbing', name: 'IDB Plumbing Solutions', href: 'https://idb-plumbing.com', desc: 'Plumbing, drains and water heaters' },
];

/**
 * Blog author (SEO Batch B, Part 6.1 — owner's exact wording, 8 Oct 2026): the author box and the
 * Article `author` Person both read "Sebastian Suciu, co-owner, IDB HVAC Solutions". No other title.
 */
export const AUTHOR = {
  name: 'Sebastian Suciu',
  title: 'co-owner',
  org: COMPANY.name,
  line: `Sebastian Suciu, co-owner, ${COMPANY.name}`,
  path: '/about',
};

export const SOUTH_SOUND_CITIES = [
  'Federal Way', 'Tacoma', 'Kent', 'Auburn', 'Renton', 'Seattle', 'Bellevue', 'Puyallup',
  'Des Moines', 'Burien', 'SeaTac', 'Tukwila', 'Covington', 'Maple Valley', 'Lakewood', 'University Place',
];

export const NORTH_SOUND_CITIES = [
  'Snohomish', 'Everett', 'Lynnwood', 'Marysville', 'Monroe', 'Lake Stevens', 'Bothell',
  'Mill Creek', 'Mukilteo', 'Edmonds', 'Arlington', 'Mountlake Terrace', 'Woodinville', 'Granite Falls',
];

export const NAV_SERVICES = [
  { label: 'Residential Installation', to: '/residential-hvac-installation', desc: 'Heat pumps, furnaces, AC, and ductless for Puget Sound homes' },
  { label: 'Commercial Installation', to: '/commercial-hvac-installation', desc: 'RTUs, VRF, make-up air, and tenant improvements' },
  { label: 'Industrial / Mechanical', to: '/industrial-hvac-installation', desc: 'Large-tonnage systems and process ventilation' },
  { label: 'Emergency Service', to: '/emergency-hvac-service', desc: 'No heat or no cooling? Priority same-day response' },
  { label: 'Maintenance & Tune-Ups', to: '/hvac-maintenance-tune-up', desc: 'Seasonal tune-ups that keep systems efficient and under warranty' },
];
// The /services overview: first item in the header dropdown ("All services") and the footer
// services column ("All Services"), ahead of the five service pages above.
export const NAV_ALL_SERVICES = { to: '/services', desc: 'Everything we install, in one place' };

/**
 * Editable copy strings (brief 01 §1). `rebate_generic` is the only sentence the site uses to talk
 * about rebate amounts — never quote a dollar figure with a date in page copy; edit it here instead.
 * The backend promo-banner default (server.py BANNER_MESSAGE_CURRENT) carries the same sentence.
 */
export const COPY = {
  rebate_generic:
    'Utility rebates vary by provider and change during the year. We\u2019re a PSE Trade Ally and we check and file your rebate for you.',
};

/** Service-desk settings shown in the UI (display-only strings; owner confirms the numbers). */
export const SERVICE = {
  diagnostic_fee: '$99', // shown on the calculator's "system is down" screen; credited to the install if we replace
  diagnosticPath: '/emergency-hvac-service', // where "Book a diagnostic visit" sends people
};

/**
 * Calculator page feature blurbs (brief 02 'Copy edits') — one set per results arm. `icon` is a
 * lucide-react icon name resolved in pages/CalculatorPage.js.
 */
export const CALC_BLURBS = {
  open: [
    { icon: 'Eye', title: 'No form before your number', text: 'You see your price first. We only ask for an email if you want the PDF, or a phone if you want to book.' },
    { icon: 'ShieldCheck', title: 'A range, never a fake price', text: 'Honest planning numbers confirmed by a free on-site measure.' },
    { icon: 'Ruler', title: 'Confirmed on site, free', text: 'A free visit with a Manual J load calculation turns the range into a signed quote.' },
  ],
  gated: [
    { icon: 'Clock', title: 'About 2 minutes', text: 'One question per screen. Go back and change any answer.' },
    { icon: 'Lock', title: 'No early contact gate', text: 'We only ask who you are at the very end — to deliver your estimate.' },
    { icon: 'ShieldCheck', title: 'A range, never a fake price', text: 'Honest planning numbers confirmed by a free on-site measure.' },
  ],
};

/** Open-mode capture cards under the number (brief 02 'open mode flow'). */
export const CALC_CAPTURE = {
  save: {
    title: 'Save this estimate',
    micro: 'No phone number needed. We\u2019ll send a PDF with everything above.',
    button: 'Email me the PDF',
    sent: 'Sent to',
  },
  lock: {
    title: 'Lock in your number',
    body: 'Book a free on-site measure and we\u2019ll turn this range into a firm, signed quote.',
    button: 'Book my free measure',
  },
  call: 'Prefer to talk? Call',
  privacy: 'We only use this to deliver your estimate and follow up about it.',
};

/**
 * Trust strip items (brief 01 §2) — rendered in this order by <TrustBar /> (Shared.js) on the home
 * page and under the calculator. Reorder or edit here; `icon` is a lucide-react icon name resolved in
 * Shared.js (BadgeCheck, ShieldCheck, Award, Thermometer, Handshake, ClipboardCheck).
 */
export const TRUST_ITEMS = [
  { key: 'license', label: `WA Lic. #${COMPANY.license}`, icon: 'BadgeCheck', emphasis: true },
  { key: 'bonded', label: 'Licensed, Bonded & Insured', icon: 'ShieldCheck' },
  { key: 'mitsubishi', label: 'Mitsubishi factory-trained', icon: 'Award' },
  { key: 'epa', label: 'EPA 608 certified', icon: 'Thermometer' },
  { key: 'pse', label: 'PSE Trade Ally', icon: 'Handshake' },
  { key: 'permits', label: 'Permit pulled & closed on every install', icon: 'ClipboardCheck' },
];

// Branded imagery rendered with the real IDB logo (fleet wraps carry the
// actual mark, phone, website, and license line).
export const IMAGES = {
  heroHome: '/images/hero-home.webp',
  residential: '/images/residential.webp',
  commercial: '/images/commercial.webp',
  industrial: '/images/industrial.webp',
  emergency: '/images/emergency.webp',
  tuneup: '/images/tuneup.webp',
  fleet: '/images/fleet.webp',
  about: '/images/stock-spiral-duct.webp',
  federalWay: '/images/stock-seattle-skyline.webp',
  snohomish: '/images/stock-snohomish-estuary.webp',
  mechanicalRoom: '/images/stock-mechanical-room.webp',
  piping: '/images/stock-industrial-piping.webp',
  servicesHero: '/images/stock-dark-industrial.webp',
  financing: '/images/stock-financing.webp',
  darkIndustrial: '/images/stock-dark-industrial.webp',
};

// Before/After pairs for the Projects gallery. Each pair is rendered from the
// same viewpoint so the transformation reads honestly.
export const BEFORE_AFTER = [
  {
    slug: 'furnace-closet',
    title: 'Hall-closet furnace, retired',
    location: 'Federal Way',
    segment: 'Residential',
    detail: 'A 25-year-old gas furnace swapped for a slim high-efficiency heat pump air handler — sealed plenum, insulated linesets, labeled condensate, patched drywall.',
    before: '/images/ba-furnace-closet-before.webp',
    after: '/images/ba-furnace-closet-after.webp',
    beforeAlt: 'Aged rusty gas furnace in a cramped hallway closet with tangled wiring',
    afterAlt: 'New heat pump air handler cleanly installed in the same closet with tidy insulated lines',
  },
  {
    slug: 'condenser-pad',
    title: 'Side-yard condenser done right',
    location: 'Tacoma',
    segment: 'Residential',
    detail: 'Rusted-out AC on a cracked pad replaced with a level heat pump condenser on a composite pad, with the lineset run in clean line-hide.',
    before: '/images/ba-condenser-pad-before.webp',
    after: '/images/ba-condenser-pad-after.webp',
    beforeAlt: 'Rusty crooked AC condenser on a cracked pad surrounded by weeds',
    afterAlt: 'New heat pump condenser level on a composite pad with a clean lineset cover',
  },
  {
    slug: 'rooftop-rtu',
    title: 'Rooftop unit changeout',
    location: 'Kent',
    segment: 'Commercial',
    detail: 'An end-of-life RTU with tar-patched flashing craned off and replaced on a new insulated curb — sealed transition, clean electrical whip, dry membrane.',
    before: '/images/ba-rooftop-rtu-before.webp',
    after: '/images/ba-rooftop-rtu-after.webp',
    beforeAlt: 'Heavily rusted rooftop HVAC unit with patched curb flashing',
    afterAlt: 'Factory-new rooftop unit on a clean insulated curb with sealed ductwork',
  },
  {
    slug: 'attic-duct',
    title: 'Attic duct system rebuilt',
    location: 'Auburn',
    segment: 'Residential',
    detail: 'Crushed, leaking flex runs replaced with a rigid insulated trunk, sealed sheet-metal takeoffs, and properly strapped branch runs.',
    before: '/images/ba-attic-duct-before.webp',
    after: '/images/ba-attic-duct-after.webp',
    beforeAlt: 'Tangled crushed flex ducts sagging across an attic floor',
    afterAlt: 'Neat rigid duct trunk with sealed takeoffs and supported flex runs in the same attic',
  },
  {
    slug: 'mechanical-room',
    title: 'Mechanical room, modernized',
    location: 'Renton',
    segment: 'Commercial',
    detail: 'A 30-year-old boiler and corroded piping replaced with a compact high-efficiency system — labeled valves, manifold, conduit runs, painted floor.',
    before: '/images/ba-mechanical-room-before.webp',
    after: '/images/ba-mechanical-room-after.webp',
    beforeAlt: 'Old boiler with corroded pipes and clutter in a dim mechanical room',
    afterAlt: 'Modern compact mechanical system with labeled piping and a painted floor in the same room',
  },
  {
    slug: 'garage-air-handler',
    title: 'Garage electric furnace upgrade',
    location: 'Everett',
    segment: 'Residential',
    detail: 'A dented electric furnace with exposed splices swapped for a wall-hung air handler — sealed duct connections, straight conduit, labeled disconnect.',
    before: '/images/ba-garage-air-handler-before.webp',
    after: '/images/ba-garage-air-handler-after.webp',
    beforeAlt: 'Old dented electric furnace with exposed wiring on a garage wall',
    afterAlt: 'New wall-hung air handler with tidy conduit and sealed ducts on the same garage wall',
  },
];

export const PROJECT_IMAGES = [
  { src: '/images/stock-rooftop-units.webp', alt: 'Rows of large-tonnage rooftop cooling equipment on a commercial installation' },
  { src: '/images/stock-refrigerant-charge.webp', alt: 'Technician verifying refrigerant charge on a newly installed heat pump outdoor unit' },
  { src: '/images/stock-mechanical-room.webp', alt: 'Clean mechanical room with precision piping and valve assemblies' },
  { src: '/images/stock-supply-duct.webp', alt: 'Labeled supply duct trunk installed in a commercial ceiling' },
  { src: '/images/stock-industrial-piping.webp', alt: 'Precision pump and piping assembly on an industrial mechanical system' },
  { src: '/images/stock-spiral-duct.webp', alt: 'Polished spiral steel ductwork installed at ceiling level' },
  { src: '/images/stock-hydronic-manifold.webp', alt: 'Hydronic manifold with temperature gauge and control valve' },
  { src: '/images/stock-seattle-skyline.webp', alt: 'Seattle skyline at dusk with Mount Rainier, near a completed rooftop unit project' },
];
